"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { CountrySelect } from "@/components/country";
import { iso2to3, iso3to2 } from "@/lib/iso3";
import { provideSenderInformationAction } from "@/app/actions";
import type { Originator } from "@/lib/conduit";

/** CountrySelect (ISO2 UI) storing ISO3, as Conduit's compliance objects expect. */
export function Iso3CountryField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (iso3: string) => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <CountrySelect value={value ? iso3to2(value) : ""} onChange={(c) => onChange(iso2to3(c))} />
    </div>
  );
}

/**
 * Inline resolution for a deposit held awaiting sender information (travel
 * rule): identify the originator and, by default, also register the address
 * so future deposits from it clear straight through.
 */
export default function SenderInfoForm({
  transactionId,
  sourceAddress,
}: {
  transactionId: string;
  sourceAddress?: string;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const [f, setF] = useState({
    entityType: "business" as "business" | "individual",
    legalName: "",
    country: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    countryOfCitizenship: "",
    register: true,
    label: "",
  });
  function set<K extends keyof typeof f>(k: K, v: (typeof f)[K]) {
    setF((x) => ({ ...x, [k]: v }));
  }

  const complete =
    f.entityType === "business"
      ? !!f.legalName && !!f.country
      : !!f.firstName && !!f.lastName && !!f.dateOfBirth && !!f.countryOfCitizenship;

  function submit() {
    setError(null);
    const originator: Originator =
      f.entityType === "business"
        ? { entityType: "business", legalName: f.legalName, country: f.country }
        : {
            entityType: "individual",
            firstName: f.firstName,
            lastName: f.lastName,
            dateOfBirth: f.dateOfBirth,
            countryOfCitizenship: f.countryOfCitizenship,
          };
    start(async () => {
      const res = await provideSenderInformationAction(transactionId, {
        originator,
        register: f.register,
        ...(f.label ? { label: f.label } : {}),
      });
      if (res.ok) {
        setDone(true);
        router.refresh();
      } else setError(res.error);
    });
  }

  if (done) {
    return (
      <p className="text-[14px] font-light">
        Sender information submitted — the deposit clears once compliance re-screens it (usually minutes).
      </p>
    );
  }

  return (
    <div>
      {sourceAddress && (
        <p className="mono mb-3 break-all text-[12px]">sender {sourceAddress}</p>
      )}
      <div className="mb-3 flex gap-2">
        {(["business", "individual"] as const).map((t) => (
          <button
            key={t}
            type="button"
            className="pill"
            style={
              f.entityType === t
                ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
            }
            onClick={() => set("entityType", t)}
          >
            sender is a {t}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {f.entityType === "business" ? (
          <>
            <div>
              <label className="label">Legal name</label>
              <input className="field" value={f.legalName} onChange={(e) => set("legalName", e.target.value)} />
            </div>
            <Iso3CountryField label="Country" value={f.country} onChange={(v) => set("country", v)} />
          </>
        ) : (
          <>
            <div>
              <label className="label">First name</label>
              <input className="field" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} />
            </div>
            <div>
              <label className="label">Last name</label>
              <input className="field" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} />
            </div>
            <div>
              <label className="label">Date of birth</label>
              <input className="field" type="date" value={f.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} />
            </div>
            <Iso3CountryField
              label="Country of citizenship"
              value={f.countryOfCitizenship}
              onChange={(v) => set("countryOfCitizenship", v)}
            />
          </>
        )}
      </div>

      <label className="mt-3 flex items-start gap-3 text-[13px] font-light">
        <input type="checkbox" className="mt-1" checked={f.register} onChange={(e) => set("register", e.target.checked)} />
        <span>Also whitelist this address — future deposits from it clear without this hold.</span>
      </label>
      {f.register && (
        <div className="mt-2 max-w-xs">
          <input
            className="field"
            placeholder="Label (optional), e.g. 12835304 treasury"
            value={f.label}
            onChange={(e) => set("label", e.target.value)}
          />
        </div>
      )}

      <button className="btn btn-dark mt-4" disabled={pending || !complete} onClick={submit}>
        {pending ? "Submitting…" : "Submit sender information"}
      </button>

      {error && (
        <div className="card-cream mt-3 p-3 text-[13px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
